import React from 'react';

/**
 * Skeleton primitives.
 *
 * Every loading state uses these — replaces spinners and empty grey boxes.
 * All variants share the same shimmer animation so the app feels consistent.
 *
 * UX rule applied: the skeleton shape MUST match the content it replaces
 * (card = card, chart = chart) — so the layout doesn't jump on load.
 */

// Base shimmer block. Uses CSS gradient animation defined in premium.css.
export const Skeleton = ({ className = '', style, testid }) => (
  <div
    className={`skeleton-shimmer rounded-lg ${className}`}
    style={style}
    data-testid={testid}
    aria-hidden="true"
  />
);

// A KPI stat card skeleton — matches the real KPI card shape (label + big number).
export const SkeletonStat = ({ testid }) => (
  <div
    className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 flex flex-col gap-4"
    data-testid={testid}
    aria-hidden="true"
  >
    <div className="flex items-center justify-between">
      <Skeleton className="h-2.5 w-16" />
      <Skeleton className="h-4 w-4 rounded-md" />
    </div>
    <Skeleton className="h-8 w-24" />
    <Skeleton className="h-2 w-20" />
  </div>
);

// A generic card skeleton (title + 2 lines of body).
export const SkeletonCard = ({ lines = 3, testid }) => (
  <div
    className="rounded-2xl border border-white/5 bg-white/[0.02] p-5 space-y-3"
    data-testid={testid}
    aria-hidden="true"
  >
    <Skeleton className="h-4 w-2/3" />
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton key={i} className={`h-2.5 ${i === lines - 1 ? 'w-1/2' : 'w-full'}`} />
    ))}
  </div>
);

// A video / clip row skeleton (thumbnail + title + meta).
export const SkeletonVideoRow = ({ testid }) => (
  <div
    className="flex items-center gap-4 p-4 rounded-xl border border-white/5 bg-white/[0.02]"
    data-testid={testid}
    aria-hidden="true"
  >
    <Skeleton className="w-20 h-20 rounded-lg shrink-0" />
    <div className="flex-1 space-y-2.5">
      <Skeleton className="h-4 w-3/5" />
      <Skeleton className="h-2.5 w-2/5" />
    </div>
    <Skeleton className="h-8 w-24 rounded-lg" />
  </div>
);

// A clip tile skeleton (9:16 preview).
export const SkeletonClipTile = ({ testid }) => (
  <div className="space-y-2" data-testid={testid} aria-hidden="true">
    <Skeleton className="aspect-[9/16] w-full rounded-xl" />
    <Skeleton className="h-3 w-3/4" />
    <Skeleton className="h-2 w-1/3" />
  </div>
);

// A chart skeleton (line + axis dots).
export const SkeletonChart = ({ height = 200, testid }) => (
  <div
    className="rounded-2xl border border-white/5 bg-white/[0.02] p-6"
    style={{ minHeight: height + 60 }}
    data-testid={testid}
    aria-hidden="true"
  >
    <div className="flex justify-between mb-6">
      <Skeleton className="h-3 w-32" />
      <Skeleton className="h-3 w-20" />
    </div>
    <div className="relative" style={{ height }}>
      <svg viewBox="0 0 400 180" className="w-full h-full">
        {[0, 1, 2, 3].map((i) => (
          <line
            key={i}
            x1="0" x2="400"
            y1={i * 60 + 20} y2={i * 60 + 20}
            stroke="#ffffff08" strokeDasharray="2 4"
          />
        ))}
        <path
          d="M 0 140 C 60 120, 100 90, 140 100 S 220 60, 260 80 S 340 40, 400 55"
          fill="none"
          stroke="url(#skel-grad)"
          strokeWidth="2.5"
          className="skeleton-line"
        />
        <defs>
          <linearGradient id="skel-grad" x1="0" x2="1">
            <stop offset="0%" stopColor="#3f3f46" />
            <stop offset="50%" stopColor="#52525b" />
            <stop offset="100%" stopColor="#3f3f46" />
          </linearGradient>
        </defs>
      </svg>
    </div>
  </div>
);

// A radar chart skeleton (pentagon).
export const SkeletonRadar = ({ size = 280, testid }) => (
  <div className="flex justify-center" data-testid={testid} aria-hidden="true">
    <svg viewBox={`0 0 ${size} ${size}`} className="w-full max-w-md skeleton-shimmer-svg">
      {[0.35, 0.65, 1].map((r, i) => (
        <polygon
          key={i}
          points={Array.from({ length: 5 }).map((_, j) => {
            const a = -Math.PI / 2 + (j * 2 * Math.PI) / 5;
            const rr = ((size / 2) - 40) * r;
            return `${size / 2 + rr * Math.cos(a)},${size / 2 + rr * Math.sin(a)}`;
          }).join(' ')}
          fill="none" stroke="#ffffff10"
        />
      ))}
    </svg>
  </div>
);

// A timeline row skeleton (list of dated entries).
export const SkeletonTimeline = ({ rows = 4, testid }) => (
  <div className="space-y-3" data-testid={testid} aria-hidden="true">
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-3 rounded-lg border border-white/5 bg-white/[0.015]">
        <Skeleton className="w-10 h-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-2 w-1/3" />
        </div>
        <Skeleton className="w-16 h-6 rounded-md" />
      </div>
    ))}
  </div>
);

// A list skeleton (N stacked rows). Handy for insights/goals/achievements.
export const SkeletonList = ({ rows = 3, testid }) => (
  <div className="space-y-3" data-testid={testid} aria-hidden="true">
    {Array.from({ length: rows }).map((_, i) => <SkeletonCard key={i} lines={2} />)}
  </div>
);
